'use client';
import * as React from 'react';
import { cn } from '@/lib/utils';

function TooltipProvider({ children }: { children: React.ReactNode; delayDuration?: number }) {
  return <>{children}</>;
}

function Tooltip({
  children,
  delayDuration,
  style,
  className,
  ...rest
}: {
  children: React.ReactNode;
  delayDuration?: number;
  style?: React.CSSProperties;
  className?: string;
}) {
  return (
    <span className={cn('group/tooltip relative inline-grid', className)} style={style}>
      {children}
    </span>
  );
}

function TooltipTrigger({ children, asChild }: { children: React.ReactNode; asChild?: boolean }) {
  return <>{children}</>;
}

function getPositionClasses(side: string, align?: string) {
  const isVertical = side === 'top' || side === 'bottom';
  if (isVertical) {
    const main = side === 'top' ? 'bottom-full mb-2' : 'top-full mt-2';
    const cross = align === 'start' ? 'left-0' : align === 'end' ? 'right-0' : 'left-1/2 -translate-x-1/2';
    return `${main} ${cross}`;
  }
  const main = side === 'left' ? 'right-full mr-2' : 'left-full ml-2';
  const cross = align === 'start' ? 'top-0' : align === 'end' ? 'bottom-0' : 'top-1/2 -translate-y-1/2';
  return `${main} ${cross}`;
}

function TooltipContent({
  children,
  className,
  side = 'top',
  align,
  sideOffset,
  ...rest
}: {
  children: React.ReactNode;
  className?: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  [key: string]: unknown;
}) {
  const ref = React.useRef<HTMLSpanElement>(null);
  const shiftRef = React.useRef({ x: 0, y: 0 });
  const [shift, setShift] = React.useState({ x: 0, y: 0 });
  const [cappedWidth, setCappedWidth] = React.useState<number | null>(null);
  const [cappedHeight, setCappedHeight] = React.useState<number | null>(null);

  // The tooltip is positioned purely with CSS relative to its trigger, so a trigger near
  // the window edge pushes it off-screen where it gets clipped. Nudge it back in-view on
  // hover/focus.
  React.useEffect(() => {
    const node = ref.current;
    const group = node?.parentElement;
    if (!node || !group) return;

    const reposition = () => {
      const padding = 8;
      // Ancestors with overflow other than visible clip the tooltip too (e.g. the
      // scrollable main pane), so clamp against the intersection of the viewport
      // and every clipping ancestor's box.
      let minLeft = 0;
      let maxRight = window.innerWidth;
      let minTop = 0;
      let maxBottom = window.innerHeight;
      for (let el = group.parentElement; el; el = el.parentElement) {
        const { overflowX, overflowY } = getComputedStyle(el);
        if (overflowX !== 'visible' || overflowY !== 'visible') {
          const r = el.getBoundingClientRect();
          minLeft = Math.max(minLeft, r.left);
          maxRight = Math.min(maxRight, r.right);
          minTop = Math.max(minTop, r.top);
          maxBottom = Math.min(maxBottom, r.bottom);
        }
      }
      const availableW = maxRight - minLeft - padding * 2;
      const availableH = maxBottom - minTop - padding * 2;
      const rect = node.getBoundingClientRect();
      // Narrower/shorter than the clip area is a precondition for fitting it back in view.
      setCappedWidth(rect.width > availableW ? availableW : null);
      setCappedHeight(rect.height > availableH ? availableH : null);
      const left = rect.left - shiftRef.current.x;
      const right = rect.right - shiftRef.current.x;
      const top = rect.top - shiftRef.current.y;
      const bottom = rect.bottom - shiftRef.current.y;
      let nextX = 0;
      if (left < minLeft + padding) nextX = minLeft + padding - left;
      else if (right > maxRight - padding) nextX = maxRight - padding - right;
      let nextY = 0;
      if (top < minTop + padding) nextY = minTop + padding - top;
      else if (bottom > maxBottom - padding) nextY = maxBottom - padding - bottom;
      if (nextX !== shiftRef.current.x || nextY !== shiftRef.current.y) {
        shiftRef.current = { x: nextX, y: nextY };
        setShift({ x: nextX, y: nextY });
      }
    };

    // Meet direct én in het volgende frame: de eerste meting kan plaatsvinden vóór de
    // layout rond is (fonts/reflow), waardoor de correctie anders één hover achterloopt.
    const repositionTwice = () => {
      reposition();
      requestAnimationFrame(reposition);
    };

    repositionTwice();
    group.addEventListener('pointerenter', repositionTwice);
    group.addEventListener('focusin', repositionTwice);
    window.addEventListener('resize', reposition);
    return () => {
      group.removeEventListener('pointerenter', repositionTwice);
      group.removeEventListener('focusin', repositionTwice);
      window.removeEventListener('resize', reposition);
    };
  }, []);

  return (
    <span
      ref={ref}
      role="tooltip"
      translate="no"
      style={{
        // standalone `translate`, so it composes with (instead of overriding) the
        // -translate-x-1/2 centering that Tailwind emits as `transform`
        ...(shift.x || shift.y ? { translate: `${shift.x}px ${shift.y}px` } : null),
        ...(cappedWidth != null ? { maxWidth: `${cappedWidth}px` } : null),
        ...(cappedHeight != null ? { maxHeight: `${cappedHeight}px` } : null),
      }}
      className={cn(
        'absolute z-50 pointer-events-none',
        // w-max: absolute positioning shrink-wraps to the trigger's width (the containing block),
        // so tooltips on narrow labels rendered as tall single-word columns without it.
        'w-max max-w-sm',
        'overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md',
        'opacity-0 transition-opacity duration-150',
        'group-hover/tooltip:opacity-100 group-focus-within/tooltip:opacity-100',
        getPositionClasses(side, align),
        className,
      )}
    >
      {children}
    </span>
  );
}

TooltipContent.displayName = 'TooltipContent';

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
